import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Info, Loader2, Package, Clock, Shield, Palette } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useCart } from '../../context/CartContext';
import { smallFormatAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import RecentlyViewed from '../../components/RecentlyViewed';

const SmallFormatProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { recentlyViewed, addToRecentlyViewed } = useRecentlyViewed();
  
  // Product state
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Options state
  const [quantity, setQuantity] = useState(100);
  const [selectedOptions, setSelectedOptions] = useState({});

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await smallFormatAPI.getProduct(productId);
        const productData = data.product || data;
        setProduct(productData);
        
        // Initialize options from product
        const initialOptions = {};
        if (productData.options) {
          if (Array.isArray(productData.options)) {
            // List format: [{name: 'Size', values: [...]}]
            productData.options.forEach(opt => {
              if (opt.values && opt.values.length > 0 && opt.name !== 'Quantity') {
                const key = opt.name.replace(/\s+/g, '_');
                initialOptions[key] = opt.values[0];
              }
            });
          } else {
            // Object format: {Size: [...]}
            Object.keys(productData.options).forEach(key => {
              if (Array.isArray(productData.options[key]) && productData.options[key].length > 0) {
                initialOptions[key] = productData.options[key][0];
              }
            });
          }
        }
        // For option_matrix products, initialize first option of each dimension
        if (productData.pricing_type === 'option_matrix' && productData.option_matrix) {
          Object.keys(productData.option_matrix).forEach(key => {
            if (!initialOptions[key]) {
              const options = Object.keys(productData.option_matrix[key] || {});
              if (options.length > 0) initialOptions[key] = options[0];
            }
          });
        }
        setSelectedOptions(initialOptions);
        
        // Set initial quantity
        setQuantity(productData.min_quantity || productData.minQuantity || 100);
        
        // Add to recently viewed
        addToRecentlyViewed({
          ...productData,
          section: 'small-format'
        });
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [productId, addToRecentlyViewed]);

  // Calculate price based on options and quantity
  const calculatedPrice = useMemo(() => {
    if (!product) return 0;
    
    // For option_matrix pricing (like business cards)
    if (product.pricing_type === 'option_matrix' && product.option_matrix) {
      // Navigate through the option matrix to find price
      let priceData = product.option_matrix;
      for (const [key, value] of Object.entries(selectedOptions)) {
        if (priceData && priceData[key] && priceData[key][value]) {
          priceData = priceData[key][value];
        }
      }
      // If we found a number, use it; otherwise use retail_price
      if (typeof priceData === 'number') {
        return priceData;
      }
    }
    
    // For price_matrix with variants
    if (product.price_matrix && Array.isArray(product.price_matrix)) {
      // Find matching variant
      const variant = product.price_matrix.find(v => {
        return Object.entries(selectedOptions).every(([key, value]) => 
          v[key] === value || v[key.toLowerCase()] === value
        );
      });
      if (variant && variant.retail_price) {
        return variant.retail_price;
      }
    }
    
    // Fallback to retail_price
    return product.retail_price || product.basePrice || 0;
  }, [product, selectedOptions]);

  const totalPrice = useMemo(() => {
    return calculatedPrice * quantity;
  }, [calculatedPrice, quantity]);

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionType]: value
    }));
  };

  const handleAddToCart = () => {
    if (!product) return;

    const cartProduct = {
      ...product,
      basePrice: calculatedPrice
    };

    const options = {
      ...selectedOptions,
      quantity: quantity
    };

    addToCart(cartProduct, options, 1);
    toast({
      title: "Added to cart!",
      description: `${product.name} (${quantity} pcs) has been added to your cart.`,
      duration: 3000,
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  // Quantity options for small format (typical print runs)
  const quantityOptions = [50, 100, 250, 500, 1000, 2500, 5000];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-600 animate-spin mx-auto mb-4" />
          <p className="text-ivory-500 font-body">Loading product...</p>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Card className="bg-ivory-100 border border-ivory-300/50">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-ivory-300 mx-auto mb-4" />
            <p className="text-ivory-600 text-lg mb-4">Product not found</p>
            <Button 
              className="btn-luxury-primary" 
              asChild
            >
              <Link to="/SmallPrint/products">Browse Products</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const features = [
    { icon: Package, text: 'Premium paper stocks' },
    { icon: Clock, text: '3-5 day turnaround' },
    { icon: Shield, text: 'Quality guaranteed' },
    { icon: Palette, text: 'Full color printing' },
  ];

  return (
    <div className="min-h-screen bg-ivory-50">
      <div className="container mx-auto px-4 py-8">
        <Link 
          to="/SmallPrint/products" 
          className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
          data-testid="back-to-small-format-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Product Image */}
          <div className="space-y-4">
            <Card className="bg-ivory-100 border border-ivory-300/50 overflow-hidden card-luxury">
              <div className="aspect-square bg-ivory-200">
                <img
                  src={product.image_url || product.image || 'https://placehold.co/600x600/f5f3f0/9a8b7d?text=Small+Format'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/600x600/f5f3f0/9a8b7d?text=Small+Format';
                  }}
                />
              </div>
            </Card>
            {/* Gallery thumbnails if available */}
            {product.images && product.images.length > 1 && (
              <div className="flex gap-2">
                {product.images.slice(0, 4).map((img, idx) => (
                  <div key={idx} className="w-20 h-20 rounded-lg overflow-hidden border border-ivory-300/50">
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <p className="text-gold-600 font-mono text-sm mb-2">{product.sku}</p>
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-3" data-testid="small-format-product-name">
                {product.name}
              </h1>
              <p className="text-lg text-ivory-500 leading-relaxed font-body">{product.description}</p>
            </div>

            {/* Price Display */}
            <div className="p-6 rounded-2xl gradient-champagne border border-gold-500/20">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-heading font-bold text-gold-600" data-testid="small-format-product-price">
                  ${calculatedPrice.toFixed(2)}
                </span>
                <span className="text-ivory-500 font-body">per unit</span>
              </div>
              <p className="text-sm text-ivory-500 font-body">
                Total for {quantity} units: <span className="text-ivory-800 font-bold">${totalPrice.toFixed(2)}</span>
              </p>
            </div>

            {/* Configuration Options */}
            <Card className="bg-ivory-100 border border-ivory-300/50 card-luxury">
              <CardContent className="p-6">
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-6">Configure Your Order</h3>
                <div className="space-y-5">
                  {/* Dynamic Options from Product - Handle both list and object formats */}
                  {product.options && Array.isArray(product.options) ? (
                    // List format: [{name: 'Size', values: [...]}]
                    product.options.filter(opt => opt.name !== 'Quantity').map((option) => {
                      const optionKey = option.name.replace(/\s+/g, '_');
                      const optionValues = option.values || [];
                      if (optionValues.length === 0) return null;
                      return (
                        <div key={optionKey}>
                          <Label className="text-sm font-medium text-ivory-600 mb-2 block font-body">{option.name}</Label>
                          <Select 
                            value={selectedOptions[optionKey] || optionValues[0]} 
                            onValueChange={(value) => handleOptionChange(optionKey, value)}
                          >
                            <SelectTrigger className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid={`small-format-${optionKey}-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-ivory-50 border-ivory-300">
                              {optionValues.map((val) => (
                                <SelectItem key={val} value={val} className="text-ivory-700 hover:bg-gold-500/10 font-body">{val}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })
                  ) : product.options ? (
                    // Object format: {Size: [...], Paper: [...]}
                    Object.entries(product.options).map(([optionKey, optionValues]) => {
                      if (!Array.isArray(optionValues) || optionValues.length === 0) return null;
                      const label = optionKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, str => str.toUpperCase());
                      return (
                        <div key={optionKey}>
                          <Label className="text-sm font-medium text-ivory-600 mb-2 block font-body">{label}</Label>
                          <Select 
                            value={selectedOptions[optionKey] || optionValues[0]} 
                            onValueChange={(value) => handleOptionChange(optionKey, value)}
                          >
                            <SelectTrigger className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid={`small-format-${optionKey}-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-ivory-50 border-ivory-300">
                              {optionValues.map((val) => (
                                <SelectItem key={val} value={val} className="text-ivory-700 hover:bg-gold-500/10 font-body">{val}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })
                  ) : null}

                  {/* Option Matrix dimensions (for business cards, etc.) */}
                  {product.pricing_type === 'option_matrix' && product.option_matrix && (
                    Object.keys(product.option_matrix).map((dimension) => {
                      const options = Object.keys(product.option_matrix[dimension] || {});
                      if (options.length === 0) return null;
                      const label = dimension.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, str => str.toUpperCase());
                      return (
                        <div key={dimension}>
                          <Label className="text-sm font-medium text-ivory-600 mb-2 block font-body">{label}</Label>
                          <Select 
                            value={selectedOptions[dimension] || options[0]} 
                            onValueChange={(value) => handleOptionChange(dimension, value)}
                          >
                            <SelectTrigger className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid={`small-format-${dimension}-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-ivory-50 border-ivory-300">
                              {options.map((opt) => (
                                <SelectItem key={opt} value={opt} className="text-ivory-700 hover:bg-gold-500/10 font-body">{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })
                  )}

                  {/* Quantity Selection */}
                  <div>
                    <Label className="text-sm font-medium text-ivory-600 mb-2 block font-body">Quantity</Label>
                    <Select 
                      value={quantity.toString()} 
                      onValueChange={(value) => setQuantity(parseInt(value))}
                    >
                      <SelectTrigger className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="small-format-quantity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ivory-50 border-ivory-300">
                        {quantityOptions.map((qty) => (
                          <SelectItem key={qty} value={qty.toString()} className="text-ivory-700 hover:bg-gold-500/10 font-body">
                            {qty.toLocaleString()} units
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-ivory-500 mt-2 font-body">
                      Minimum order: {product.min_quantity || product.minQuantity || 50} units
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full text-lg py-6 btn-luxury-primary rounded-xl font-body"
                onClick={handleBuyNow}
                data-testid="small-format-buy-now-btn"
              >
                Buy Now - ${totalPrice.toFixed(2)}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full text-lg py-6 btn-luxury-outline rounded-xl font-body"
                onClick={handleAddToCart}
                data-testid="small-format-add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-xl bg-ivory-100 border border-ivory-300/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-gold-600" />
                  </div>
                  <span className="text-sm text-ivory-600 font-body">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Specifications */}
            {product.specifications && (
              <Card className="bg-ivory-100 border border-ivory-300/50 card-luxury">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-gold-600" />
                    <h3 className="font-heading font-semibold text-lg text-ivory-800">Specifications</h3>
                  </div>
                  <div className="space-y-2 text-sm text-ivory-600 font-body">
                    {product.specifications.paper && (
                      <p><strong className="text-ivory-800">Paper:</strong> {product.specifications.paper}</p>
                    )}
                    {product.specifications.finish && (
                      <p><strong className="text-ivory-800">Finish:</strong> {product.specifications.finish}</p>
                    )}
                    {product.specifications.size && (
                      <p><strong className="text-ivory-800">Size:</strong> {product.specifications.size}</p>
                    )}
                    {product.specifications.printSides && (
                      <p><strong className="text-ivory-800">Print Sides:</strong> {product.specifications.printSides}</p>
                    )}
                    {product.specifications.turnaround && (
                      <p><strong className="text-ivory-800">Turnaround:</strong> {product.specifications.turnaround}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Artwork Note */}
            <Card className="bg-gold-500/10 border border-gold-500/20">
              <CardContent className="p-4">
                <p className="text-sm text-ivory-700 font-body">
                  <strong>Artwork Note:</strong> Please upload print-ready files in PDF, AI, or high-resolution JPG format. 
                  Contact us if you need design assistance.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recently Viewed Section */}
        <RecentlyViewed products={recentlyViewed} currentProductId={productId} />
      </div>
    </div>
  );
};

export default SmallFormatProductDetail;
